import { PropertyFilter, PropertyFilterProps } from "@amzn/awsui-components-react";
import { trackCustomEvent, useTrackerId } from "@amzn/uxm-aws-ui-components-react/lib/src/uxm/custom-event";
import FormFieldWrapper from "../CustomFormField/FormFieldWrapper/FormFieldWrapper";
import { CustomPropertyFilterFormFieldProps } from "interfaces";
import { getString } from "../../ui_string_labels/ui-strings-utils";
import uiStrings from "../../ui_string_labels/ui-strings.json";
import { useMemo } from "react";

const getStringPath = "common.component.propertyFilter.dynamicText";

/**
 * Custom hook to retrieve the property filter strings
 */
export const usePropertyFilterStrings = (): PropertyFilterProps.I18nStrings => {
    return useMemo(
        () => ({
            ...uiStrings.common.component.propertyFilter.staticText,
            enteredTextLabel: (text) => getString("enteredTextLabel", getStringPath, { text }),
            removeTokenButtonAriaLabel: (token) =>
                getString("removeTokenButtonAriaLabel", getStringPath, {
                    token: token.propertyLabel,
                }),
            groupEditAriaLabel: (group) =>
                getString("groupEditAriaLabel", getStringPath, { operation: group.operationLabel }),
            tokenEditorTokenActionsAriaLabel: (token) =>
                getString("tokenEditorTokenActionsAriaLabel", getStringPath, {
                    token: token.propertyLabel,
                }),
            tokenEditorTokenRemoveAriaLabel: (token) =>
                getString("tokenEditorTokenRemoveAriaLabel", getStringPath, {
                    token: token.propertyLabel,
                }),
            tokenEditorAddExistingTokenAriaLabel: (token) =>
                getString("tokenEditorAddExistingTokenAriaLabel", getStringPath, {
                    token: token.propertyLabel,
                }),
            tokenEditorAddExistingTokenLabel: (token) =>
                getString("tokenEditorAddExistingTokenLabel", getStringPath, {
                    token: token.propertyLabel,
                }),
        }),
        [],
    );
};

/**
 * A custom form field that enables property based filtering
 *
 * @param {string} [label] - The label text displayed above the property filter
 * @param {function} onFilterChange - Callback function triggered when filter query changes
 * @param {string} [placeholder] - Placeholder text shown in the filter input
 * @param {object} query - Current filter query object
 * @param {array} filteringProperties - Array of properties available for filtering
 * @param {boolean} [hideOperations] - Whether to hide operation buttons in the filter
 * @param {boolean} [disabled] - Whether the property filter is disabled
 * @param {string} [errorText] - Error message to display below the filter
 * @param {string} [warningText] - Warning message to display below the filter
 * @param {string} [description] - Description text displayed below the label
 * @param {string} [info] - Info text displayed as a tooltip or help text
 * @param {string} [testId] - Test identifier for automated testing
 *
 * @return {JSX.Element} - Rendered CustomPropertyFilter component wrapped in FormFieldWrapper
 */
const CustomPropertyFilter = ({
    label,
    onFilterChange,
    placeholder,
    query,
    filteringProperties,
    hideOperations = false,
    disabled = false,
    errorText,
    warningText,
    description,
    info,
    testId,
    filteringOptions,
    readOnlyOperations = false,
    touchpoint,
}: CustomPropertyFilterFormFieldProps) => {
    const i18nStrings = usePropertyFilterStrings();

    const trackerId = useTrackerId();

    const onFilterChangeWrapper = (event: { detail: PropertyFilterProps.Query }) => {
        const eventData = {
            component: "CustomPropertyFilter",
            action: "property-filter-change",
        };

        trackCustomEvent(eventData, touchpoint, trackerId);

        onFilterChange(event);
    };

    const propertyFilter = (
        <PropertyFilter
            filteringPlaceholder={placeholder}
            query={query}
            onChange={onFilterChangeWrapper}
            filteringProperties={filteringProperties}
            hideOperations={hideOperations}
            disabled={disabled}
            expandToViewport
            filteringOptions={filteringOptions}
            i18nStrings={i18nStrings}
            readOnlyOperations={readOnlyOperations}
        />
    );

    return (
        <FormFieldWrapper
            testId={testId}
            label={label}
            errorText={errorText}
            warningText={warningText}
            description={description}
            info={info}
            input={propertyFilter}
        />
    );
};

export default CustomPropertyFilter;
